import { ArrowDropDown, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { TixSelector } from '@tix-ticketing/ui-components';
import { bindToggle, usePopupState } from 'material-ui-popup-state/hooks';
import { useMemo } from 'react';
import { FormattedDate } from 'react-intl';
import type { CalendarMonthDto } from 'api/generated';
import { clientTimezone } from 'lib/helpers/date-helpers';
import type { useCalendar } from 'lib/hooks/use-calendar';
export const EventsCalendarNavigationSelector = ({
  calendar,
  year,
  setYear,
  monthOptions,
  availableMonths
}: {
  calendar: ReturnType<typeof useCalendar>;
  year: number;
  setYear: (year: number) => void;
  monthOptions: {
    value: number;
    label: string;
  }[];
  availableMonths: CalendarMonthDto[];
}) => {
  const calendarYearState = usePopupState({
    variant: 'popover',
    popupId: 'calendarYear'
  });
  const availableyYears = useMemo(() => {
    return Array.from(new Set(availableMonths.map(m => m.year).filter((y): y is number => typeof y === 'number'))).sort((a, b) => a - b);
  }, [availableMonths]);
  const prevAvailableYear = (currentYear: number) => {
    const currentYearIndex = availableyYears.indexOf(currentYear);
    setYear(availableyYears[Math.max(currentYearIndex - 1, 0)] ?? currentYear);
  };
  const nextAvailableYear = (currentYear: number) => {
    const currentYearIndex = availableyYears.indexOf(currentYear);
    setYear(availableyYears[Math.min(currentYearIndex + 1, availableyYears.length - 1)] ?? currentYear);
  };
  return <TixSelector triggerElement={<Button variant="outlined" color="monochrome" endIcon={<ArrowDropDown />} sx={theme => ({
    textTransform: 'capitalize',
    background: calendarYearState.isOpen ? theme.palette.action.hover : undefined
  })} {...bindToggle(calendarYearState)}>
          <FormattedDate value={calendar.viewing} month="long" year="numeric" timeZone={clientTimezone} />
        </Button>} open={calendarYearState.isOpen} onClose={() => {
    calendarYearState.close();
    setYear(calendar.viewing.getFullYear());
  }} localeState={calendarYearState} hideCloseButton>
      <Box sx={{
      display: 'grid',
      alignItems: 'center',
      gap: 1,
      width: '100%',
      gridTemplateColumns: 'repeat(3, 1fr)'
    }}>
        {year > Math.min(...availableyYears) ? <Button color="monochrome" onClick={() => prevAvailableYear(year)}>
            <ChevronLeft />
          </Button> : <div />}
        <Box sx={{
        py: 1.5
      }}>
          <Typography variant="h6" align="center">
            {year}
          </Typography>
        </Box>
        {year < Math.max(...availableyYears) ? <Button color="monochrome" onClick={() => nextAvailableYear(year)}>
            <ChevronRight />
          </Button> : <div />}
        {monthOptions.map(option => {
        const isDisabled = !availableMonths.find(m => m.month === option.value + 1 && m.year === year);
        return <Button key={option.value} variant={isDisabled ? 'contained' : 'outlined'} color={calendar.viewing.getMonth() === option.value && calendar.viewing.getFullYear() === year ? 'primary' : 'monochrome'} size="medium" onClick={() => {
          calendar.setViewing(new Date(year, option.value));
          calendarYearState.close();
        }} fullWidth disabled={isDisabled}>
              {option.label}
            </Button>;
      })}
      </Box>
    </TixSelector>;
};